// @ts-strict-ignore
import * as React from 'react';
import styles from './styles/appStyles';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import * as Storage from '../helpers/localStorageHelper';
import { Content } from 'antd/lib/layout/layout';
import FormItem from 'antd/lib/form/FormItem';
import Input from 'antd/lib/input';
import Checkbox from 'antd/lib/checkbox/Checkbox';
import Button from 'antd/lib/button';
import Form, { useForm } from 'antd/lib/form/Form';
import Space from 'antd/lib/space';
import Title from 'antd/lib/typography/Title';
import Text from 'antd/lib/typography/Text';
import { hubSpotUserToken } from '../helpers';
import * as AnalyticEvents from '../utils/analytics';
import Divider from 'antd/lib/divider';
import { Link, RouteComponentProps, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { tryLogin, TRY_LOGIN } from '../actions/login';
import { RedirectState } from '../typescriptHelpers';
import { runwayColors } from './styles/globalStyles';
import routes from '../routes';
import ThirdPartySSOButtons, { ThirdPartyAuthButtonVariant } from './login-signup/ThirdPartySSOButtons';
import ForgotPasswordModal from './login-signup/ForgotPasswordModal';
import { selectAuthRedirectUrl } from '../selectors/selectors';
import { RootState } from '../reducers/reducers';
import { globalMessage } from '../utils/globalMessage';
import { isMobileOnly } from 'react-device-detect';
import TermsAndConditions from './login-signup/TermsAndConditions';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import * as Cookie from 'cookie';
import { STAPE_ID_COOKIE_KEY } from '@/constants';

const LoginView: React.FC<RouteComponentProps> = (props: RouteComponentProps) => {
    const { location } = props;
    const dispatch = useDispatch();
    const history = useHistory();
    const [form] = useForm();

    const cookies = Cookie.parse(document.cookie);

    const authRedirectUrl = useSelector((state: RootState) => selectAuthRedirectUrl(state));

    const [showForgotPasswordModal, setShowForgotPasswordModal] = React.useState(false);

    const loginLoading = useIsLoading(TRY_LOGIN);
    const loginError = useError(TRY_LOGIN);
    const loginSuccess = useUpdated(TRY_LOGIN);

    const redirectState: RedirectState = location.state;
    const redirectPath = redirectState?.fromLocation;

    const handleRememberMeValues = (isChecked: boolean, email?: string) => {
        if (isChecked && email) {
            Storage.setItem(Storage.REMEMBER_ME_STORAGE_KEY, email);
        } else {
            Storage.deleteItem(Storage.REMEMBER_ME_STORAGE_KEY);
        }
    };

    const onFinish = (values) => {
        handleRememberMeValues(values.rememberMe, values.email);

        dispatch(
            tryLogin({
                email: values.email,
                password: values.password,
                hubSpotUserToken: hubSpotUserToken(document),
                stapeId: cookies[STAPE_ID_COOKIE_KEY],
            }),
        );
    };

    const layout = {
        labelCol: { span: 8 },
        wrapperCol: { span: 24 },
    };
    const tailLayout = {
        wrapperCol: { span: 12 },
    };

    const email = Storage.getItem(Storage.REMEMBER_ME_STORAGE_KEY);

    React.useEffect(() => {
        const properties = AnalyticEvents.defaultProperties();

        AnalyticEvents.track(AnalyticEvents.Event.ViewSignIn, properties);
    }, []);

    // When sent here by an external auth flow like the MCP OAuth flow
    // persist the return-to URL so checkAndRedirectToSavedRoute sends the user back
    // there once they've logged in. Only accept same-origin URLs to prevent open redirect.
    React.useEffect(() => {
        const authRedirectRoute = new URLSearchParams(location.search).get('authRedirectRoute');
        if (authRedirectRoute) {
            const isSameOrigin =
                authRedirectRoute.startsWith('/') || authRedirectRoute.startsWith(window.location.origin);
            if (isSameOrigin) {
                Storage.setAuthRedirectRoute(authRedirectRoute);
            }
        }
    }, [location.search]);

    React.useEffect(() => {
        if (loginError) {
            globalMessage.error(loginError);
        }
    }, [loginError]);

    React.useEffect(() => {
        if (loginSuccess) {
            if (authRedirectUrl) {
                // gotta redirect to another domain

                console.debug('[REDIRECT DEBUG]', 'loginSuccess', 'redirecting to', authRedirectUrl);

                window.location.replace(authRedirectUrl);
            } else {
                const redirectTo = redirectPath ?? routes.dashboard;

                console.debug('[REDIRECT DEBUG]', 'loginSuccess', 'redirecting to', redirectTo);

                history.replace(redirectTo);
            }
        }
    }, [loginSuccess]);

    return (
        <Content style={{ ...styles.centerVerticalAndHorizontal, paddingTop: 12, paddingBottom: 12 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <Space direction="vertical" size="large">
                    <Space direction="vertical" align="center" style={{ width: '100%' }}>
                        {!isMobileOnly && <Title style={{ fontSize: 24, lineHeight: '24px' }}>Log in</Title>}
                        <Text type="secondary">
                            Don&apos;t have an account yet? <Link to={routes.signup}>Sign up</Link>
                        </Text>
                    </Space>
                    <ThirdPartySSOButtons {...props} variant={ThirdPartyAuthButtonVariant.Login} />
                </Space>
                <Divider />
                <Form
                    layout="vertical"
                    onFinish={onFinish}
                    {...layout}
                    initialValues={{ email: email, rememberMe: !!email }}
                    style={{ width: 300 }}
                    form={form}
                    id="login-email-password"
                    className="runway-form-email-password"
                >
                    <FormItem name="email" rules={[{ required: true, message: 'Please enter your email' }]}>
                        <Input prefix={<UserOutlined className="site-form-item-icon" />} placeholder="Email" />
                    </FormItem>
                    <FormItem name="password" rules={[{ required: true, message: 'Please enter your password' }]}>
                        <Input
                            prefix={<LockOutlined className="site-form-item-icon" />}
                            type="password"
                            placeholder="Password"
                        />
                    </FormItem>
                    <FormItem
                        name="rememberMe"
                        valuePropName="checked"
                        {...tailLayout}
                        style={{ color: runwayColors.info }}
                    >
                        <Checkbox>Remember me</Checkbox>
                    </FormItem>
                    <FormItem>
                        <Button type="primary" htmlType="submit" style={{ width: '100%' }} loading={loginLoading}>
                            Log in
                        </Button>
                    </FormItem>
                    <FormItem>
                        <Button type="link" onClick={() => setShowForgotPasswordModal(true)} style={{ width: '100%' }}>
                            Forgot your password?
                        </Button>
                    </FormItem>
                </Form>
                <Divider>
                    <Text type="secondary">or</Text>
                </Divider>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
                    <Link to="/login/sso">Single Sign-On / SAML</Link>
                </div>
                <TermsAndConditions />
            </div>
            <ForgotPasswordModal
                open={showForgotPasswordModal}
                email={form.getFieldValue('email')}
                onClose={() => setShowForgotPasswordModal(false)}
                onSuccess={() => setShowForgotPasswordModal(false)}
            />
        </Content>
    );
};

export default LoginView;
